import { html, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../BaseElement";
import { LightTheme } from "../ThemeLight";

import { styles } from "./UserProfileGallery.style";
import { IUserInfo } from "../../backend/cloud/UserTable";
import { IMedia, IPlace, IPlaceMediaItem, showDeleteMediaDialog } from "../../../main";
import { watch } from "../internal/watch";

import { grid } from "@lit-labs/virtualizer/layouts/grid.js";
import { litIf } from "../LitUtils";
import {
    getIndexOfMediaInMediaArray,
    IPlaceMediaTable,
    userCanEditPlaceMedia,
} from "../../backend/IPlaceMediaTable";

import { cloneDeep, orderBy } from "lodash-es";
import { IPlaceTable } from "../../backend/IPlaceTable";
import { eachLimit } from "async";
import SlResizeObserver from "@shoelace-style/shoelace/dist/components/resize-observer/resize-observer.component";

import "../ProgressSpinner/ProgressSpinner";
import { formatDate } from "../../Format";
import { showMediaEditorDialog } from "../MediaEditorDialog/MediaEditorDialog";
import { ChangeSet, ChangeSetListener } from "../../DataStore/ChangeSet";
import { DataProvider } from "../../DataStore/DataProvider";

type PlaceMediaItem = { item: IPlaceMediaItem; place: IPlace };

@customElement("ds-user-profile-gallery")
export class UserProfileGallery extends BaseElement implements ChangeSetListener {
    static override styles = [styles, LightTheme];

    @property({ type: Object }) userInfo?: IUserInfo;
    @state() protected _userPlaceMediaItems?: PlaceMediaItem[];
    @state() protected _userMediaItems?: IMedia[];
    @state() protected _showUnattributedMedia = false;
    @state() protected _selectedItems: PlaceMediaItem[] = [];
    @state() protected _saving = false;
    @state() protected _itemWidth = `260px`;
    @state() protected _isCurrentUser = false;

    private _placeMediaTable = this._kernel.dataStore.getSource<IPlaceMediaTable>("place-media");
    private _placesTable = this._kernel.dataStore.getSource<IPlaceTable>("places");

    @watch("userInfo")
    async onUserInfoUpdate() {
        if (this.userInfo && this.userInfo.owner) {
            const userId = this.userInfo.owner;
            this._isCurrentUser = userId === this._kernel.user?.id;
            let allItems = await this._getUserMedia(userId);
            if (allItems) {
                if (this._showUnattributedMedia) {
                    allItems = allItems.filter(
                        (x) => !x.item.media.contributorId || x.item.media.contributorId === ""
                    );
                } else {
                    allItems = allItems.filter((x) => x.item.media.contributorId === userId);
                }

                this._userPlaceMediaItems = allItems;
                this._userMediaItems = this._userPlaceMediaItems.map((x) => x.item.media);
            }
        } else {
            this._userPlaceMediaItems = undefined;
            this._userMediaItems = undefined;
        }
    }

    @watch("_showUnattributedMedia")
    async onShowAllMediaUpdate() {
        this.onUserInfoUpdate();
    }

    override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this.addEventListener("ds-media-click", (e: CustomEvent) =>
            this._openMediaInGallery(e.detail.media)
        );

        this._placeMediaTable.addListener(this, []);
    }

    public onChangeSet(provider: DataProvider, _changeSet: ChangeSet<any>): void {
        if (provider.name === "place-media") {
            this.onUserInfoUpdate();
        }
    }

    protected override firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        const resizeObserver = this.shadowRoot?.getElementById(
            "resize-observer"
        ) as SlResizeObserver;
        if (resizeObserver) {
            resizeObserver.addEventListener("sl-resize", (_event: any) => {
                this._updateItemWidth();
            });
        }

        this._updateItemWidth();
    }

    private _updateItemWidth() {
        this._itemWidth = window.getComputedStyle(this).getPropertyValue("--item-width");
    }

    private async _getUserMedia(id: string): Promise<PlaceMediaItem[]> {
        const allItems = orderBy(
            Array.from(await this._placeMediaTable.allOwnedBy(id)),
            (x) => x.createdAt,
            "desc"
        );
        const items: PlaceMediaItem[] = [];
        const havePlaces = new Map<string, IPlace | undefined>();
        await eachLimit(allItems, 8, async (item: IPlaceMediaItem) => {
            const placeId = item.placeMediaId;
            if (placeId) {
                let place = undefined;
                if (havePlaces.has(placeId)) {
                    place = havePlaces.get(placeId);
                } else {
                    place = await this._placesTable.read(placeId);
                    havePlaces.set(placeId, place);
                }
                if (place) {
                    items?.push({
                        item,
                        place,
                    });
                }
            }
        });

        return orderBy(items, (x) => x.item.media.dateAdded ?? x.item.createdAt, "desc");
    }

    //TODO: Fix - this is duplicated in UserProfilePosts.ts
    private _openMediaInGallery(media: IMedia) {
        const mediaList = this._userMediaItems;
        if (mediaList && this._userPlaceMediaItems) {
            const index = getIndexOfMediaInMediaArray(mediaList, media.id);
            const placeId = this._userPlaceMediaItems[index].item.placeMediaId;

            this._raiseEvent("ds-open-gallery", {
                placeId: placeId,
                media: [this._userPlaceMediaItems[index].item],
            });
        }
    }

    private async _attributeMedia() {
        this._saving = true;
        for (const placeItem of this._selectedItems) {
            const item = cloneDeep(placeItem.item);
            item.media.contributorId = this.userInfo?.owner;
            item.media.attribution = undefined;
            await this._kernel.dataStore.getSource<IPlaceTable>("places").updateMediaItem(item);
        }
        this._saving = false;
        this._selectedItems = [];
        await this.onUserInfoUpdate();
    }

    private _renderGallery() {
        return html`<lit-virtualizer
            .layout=${grid({
                itemSize: `${this._itemWidth}` as any,
                justify: "center",
            })}
            .items=${this._userPlaceMediaItems ?? []}
            .renderItem=${(x: PlaceMediaItem) =>
                html`<div class="media-container">
                    <ds-media
                        size="thumbnail_512px"
                        .media=${x.item.media}
                        ?showEditControls=${userCanEditPlaceMedia(this._kernel.user, x.item)}
                        noHeader
                        @click=${() =>
                            this._raiseEvent("ds-media-click", {
                                media: x.item.media,
                            })}
                    ></ds-media>

                    <div class="media-footer flex-row">
                        <span>${formatDate(x.item.media.dateAdded ?? x.item.createdAt)}</span>
                        ${litIf(
                            this._showUnattributedMedia,
                            () => html`
                                <sl-switch
                                    class="attribute-button"
                                    size="small"
                                    ?checked=${this._selectedItems?.includes(x)}
                                    @sl-change=${(e: any) => {
                                        if (e.target.checked) {
                                            this._selectedItems = [...this._selectedItems, x];
                                        } else {
                                            this._selectedItems = this._selectedItems.filter(
                                                (y) => y !== x
                                            );
                                        }
                                    }}
                                ></sl-switch>
                            `
                        )}
                    </div>

                    <div class="media-header">
                        <span
                            @click=${() =>
                                (window.location.href = `/#/place/${x.item.placeMediaId}`)}
                            >${x.place.data.name}</span
                        >
                        <div class="flex-row">
                            <sl-icon-button
                                name="trash3"
                                @click=${() => {
                                    showDeleteMediaDialog(x.item.id);
                                }}
                            ></sl-icon-button>
                            <sl-icon-button
                                name="pencil-fill"
                                @click=${() => {
                                    showMediaEditorDialog({
                                        placeMedia: x.item,
                                        open: true,
                                    });
                                }}
                            ></sl-icon-button>
                        </div>
                    </div>
                </div>`}
        ></lit-virtualizer>`;
    }
    override render() {
        return html`<sl-resize-observer id="resize-observer"
            ><div class="container">
                ${litIf(
                    this._isCurrentUser || this._kernel.user?.isAdmin,
                    () =>
                        html` <div class="header flex-row">
                            <sl-switch
                                ?checked=${this._showUnattributedMedia}
                                @sl-change=${(e: any) => {
                                    this._showUnattributedMedia = e.target.checked;
                                }}
                                >Show all unattributed</sl-switch
                            >
                        </div>`
                )}
                ${litIf(
                    this._userPlaceMediaItems,
                    () => html`
                        ${litIf(
                            this._saving,
                            () => html`<ds-progress-spinner></ds-progress-spinner>`,
                            () => this._renderGallery()
                        )}
                        ${litIf(
                            this._selectedItems.length > 0,
                            () =>
                                html` <div class="media-buttons flex-row">
                                    <sl-button
                                        @click=${() => (this._selectedItems = [])}
                                        ?disabled=${this._selectedItems.length === 0 ||
                                        this._saving}
                                        >Clear selected</sl-button
                                    >
                                    <sl-button
                                        @click=${() => this._attributeMedia()}
                                        ?disabled=${this._selectedItems.length === 0}
                                        >Save selected</sl-button
                                    >
                                </div>`
                        )}
                    `
                )}
            </div>
        </sl-resize-observer>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-user-profile-gallery": UserProfileGallery;
    }
}
